import { db } from "./config";
import { collection, query, where, getDocs, writeBatch, doc } from "firebase/firestore";
import { User } from "firebase/auth";
import { normalizeDescription } from "../utils/brandUtils";
import { expensesCache } from "./firestoreOperations";

export interface NormalizationResult {
    totalProcessed: number;
    totalUpdated: number;
    changes: Array<{
        oldDescription: string;
        newDescription: string;
        id: string;
    }>;
}

/**
 * Sweeps all user expenses and normalizes descriptions.
 * Only updates records, never deletes.
 */
export const normalizeAllExpenses = async (user: User): Promise<NormalizationResult> => {
    try {
        const expensesCollection = collection(db, "expenses");
        const q = query(expensesCollection, where("userId", "==", user.uid));
        const querySnapshot = await getDocs(q);

        const batch = writeBatch(db);
        let totalUpdated = 0;
        const changes: Array<{ oldDescription: string, newDescription: string, id: string }> = [];

        querySnapshot.forEach((document) => {
            const data = document.data();
            const oldDescription = data.description || "";
            const newDescription = normalizeDescription(oldDescription);

            if (oldDescription !== newDescription) {
                const docRef = doc(db, "expenses", document.id);
                batch.update(docRef, { description: newDescription });
                totalUpdated++;
                changes.push({
                    id: document.id,
                    oldDescription,
                    newDescription
                });
            }
        });

        if (totalUpdated > 0) {
            await batch.commit();
            // Clear cache to force reload with normalized data
            expensesCache.delete(user.uid);
        }

        return {
            totalProcessed: querySnapshot.size,
            totalUpdated,
            changes
        };
    } catch (error) {
        console.error("Error during normalization:", error);
        throw error;
    }
};
